import dompurify from 'dompurify';
import PropTypes from 'prop-types';
import { createElement, useMemo } from 'react';
import invariant from 'utils/invariant';

const DEFAULT_DOMPURIFY_CONFIG = {
  // Enables newTab to function in create-html-link.js
  ADD_ATTR: ['target'],
};

/**
 * ReactHtml - Helper component that is designed to better isolate rendering of html
 * in react and also adds additional layer of santization if it was missed along the way.
 * This component uses React's dangerouslySetInnerHTML with dompurify santizing the react children.
 *
 * Note: This component utilizes dompurify's default config to sanitize HTML passed to this component
 * Customizing it's config could possibly remove the benefit we are gaining here. So be careful.
 *
 * Long term we may want to investigate something like react-html-parser which prevents usage of dangerouslySetInnerHTML all together
 *
 * @example
 * <ReactHtml>{t('learn.no_content.body_html')}</ReactHtml>
 *
 * @param {boolean} THE_DATA_HAS_BEEN_SANITIZED Skip sanitization at the client
 * @param {object} props
 * @param {string} props.children HTML string we are attempting to render
 * @param {ReactNode} [props.component=div] A React Node that will be used as the root component
 * @param {config} props.config DOMPurify.sanitize config options that can configure how we santize our HTML
 *
 * @returns {ReactElement} Returns an instance of a React Element
 */
const ReactHtml = ({
  allowIframe,
  children = '',
  component = 'div',
  config = DEFAULT_DOMPURIFY_CONFIG,
  THE_DATA_HAS_BEEN_SANITIZED,
  ...componentProps
}) => {
  if (process.env.NODE_ENV === 'development') {
    invariant(
      typeof children === 'string',
      `<ReactHtml> expected children prop to be of type string, but instead received ${typeof children}`,
    );
  }

  const outputChildren = useMemo(() => {
    if (THE_DATA_HAS_BEEN_SANITIZED) return children;
    return dompurify.sanitize(children, config);
  }, [children, config, THE_DATA_HAS_BEEN_SANITIZED]);

  return createElement(component, {
    ...componentProps,
    dangerouslySetInnerHTML: { __html: outputChildren },
  });
};

ReactHtml.propTypes = {
  children: PropTypes.string,
  component: PropTypes.node,
  // For the full docs on config refer to the dompurify documentation
  // https://github.com/cure53/DOMPurify#can-i-configure-dompurify
  // eslint-disable-next-line react/forbid-prop-types
  config: PropTypes.object,
};

export default ReactHtml;
